/**
 * Generic pill-style tabs with optional badge count.
 *
 * Usage:
 *   <PillTabs
 *     tabs={[
 *       { id: 'pending', label: 'En attente', count: 12 },
 *       { id: 'done',    label: 'Terminé',    count: 3, disabled: true },
 *     ]}
 *
 * `disabled` marks a tab that cannot be reached yet. It drops the pointer
 * cursor and ignores the click, so it reads as a step you are not on rather
 * than as a control that refuses you — the strip tells you where you are, it
 * is not a set of buttons being withheld.
 *     activeTab="pending"
 *     onChange={(tabId) => setActiveTab(tabId)}
 *   />
 */
const PillTabs = ({ tabs, activeTab, onChange }) => (
  <div className="flex gap-4 ft-sm">
    {tabs.map((tab) => {
      const isActive = activeTab === tab.id;
      // The tab you are on is never "unreachable", whatever the caller says.
      const isDisabled = Boolean(tab.disabled) && !isActive;

      return (
        <div
          key={tab.id}
          aria-disabled={isDisabled || undefined}
          className={`my-auto px-3 py-2 rounded transition-colors ${
            isDisabled ? 'cursor-default' : 'cursor-pointer'
          } ${
            isActive
              ? 'text-gray-700 bg-gray-100'
              : isDisabled
                ? 'text-gray-500'
                : 'text-gray-500 hover:text-gray-600'
          }`}
          onClick={isDisabled ? undefined : () => onChange(tab.id)}
        >
          <div className="flex gap-1 items-center">
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span className="bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px] text-xs font-medium">
                {tab.count}
              </span>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

export default PillTabs;
